import redCrosshatchUrl from '../img/red-crosshatch.png';
import type { MapInitialized, MapInstance, MapRendererApp, StyleLayer } from './types.ts';
import * as utils from './utils.ts';

const CROSSHATCH_LAYER = 'poi-crosshatch';
const POI_POLYGON_LAYER = 'polygons';
const PATTERN_NAME = 'red-crosshatch';

type FillExtrusionLayer = StyleLayer & {
  type: 'fill-extrusion';
  paint: {
    'fill-extrusion-height'?: unknown;
    [key: string]: unknown;
  };
};

type ImageDataResult = {
  data:
    | ImageBitmap
    | HTMLImageElement
    | ImageData
    | { width: number; height: number; data: Uint8Array | Uint8ClampedArray };
};

export default function DynamicPoiStyleController(app: MapRendererApp, mapInitialized: MapInitialized) {
  if (Object.prototype.hasOwnProperty.call(app.config.plugins, 'nexusDynamicPois')) {
    waitForMapIdle(mapInitialized).then(map => {
      addCrossHatchLayer(map);

      whenOpenClosedEvent(app, renderClosedPoisOnMap(map));
    });
  }
}

const waitForMapIdle = async (mapInitialized: MapInitialized): Promise<MapInstance> => {
  const map = await mapInitialized();

  return new Promise(resolve => {
    map.once('idle', () => {
      // Have to wait for polygons to load
      resolve(map);
    });
  });
};

const addCrossHatchLayer = async (map: MapInstance): Promise<void> => {
  await loadCrosshatchImage(map); // will throw if image not found
  const polygonHeight = getPolygonLayerExtrusionHeight(map);

  const crosshatchLayer = {
    id: CROSSHATCH_LAYER,
    type: 'fill-extrusion',
    source: 'llOrdinalSource', // Source containing features for all POIs?
    paint: {
      'fill-extrusion-pattern': PATTERN_NAME,
      'fill-extrusion-height': polygonHeight,
    },
    filter: ['in', 'DOES NOT EXIST', ''], // match nothing
  };

  map.addLayer(crosshatchLayer as never);

  map.moveLayer(CROSSHATCH_LAYER, POI_POLYGON_LAYER);
  map.moveLayer(POI_POLYGON_LAYER, CROSSHATCH_LAYER); // Put this layer in front of "polygons" layer
};

const loadCrosshatchImage = async (map: MapInstance): Promise<void> => {
  const image = (await map.loadImage(redCrosshatchUrl)) as ImageDataResult;
  map.addImage(PATTERN_NAME, image.data);
};

const getPolygonLayerExtrusionHeight = (map: MapInstance): FillExtrusionLayer['paint']['fill-extrusion-height'] => {
  // Polygon layer is the layer, which is responsible for POI polygon height?
  const layer = map.getStyle().layers.find(styleLayer => styleLayer.id === POI_POLYGON_LAYER) as
    | FillExtrusionLayer
    | undefined;
  return layer?.paint?.['fill-extrusion-height'];
};

const whenOpenClosedEvent = (
  app: MapRendererApp,
  fn: (event: { plugin?: string; idValuesMap?: Record<string, { isOpen?: boolean }> }) => void,
): void => {
  app.bus.monitor(
    'poi/setNexusDynamicData',
    (event: { plugin?: string; idValuesMap?: Record<string, { isOpen?: boolean }> }) => {
      if (event.plugin === 'open-closed-status') {
        fn(event);
      }
    },
  );
};

const renderClosedPoisOnMap =
  (map: MapInstance) =>
  (event: { idValuesMap?: Record<string, { isOpen?: boolean }> }): void => {
    const poiIds = utils.getClosedPois(event);

    showCrosshatchLayerForPois(map, poiIds);
  };

const showCrosshatchLayerForPois = async (map: MapInstance, poiIds: string[]): Promise<void> => {
  map.setFilter(CROSSHATCH_LAYER, ['in', 'id', ...poiIds]);
};
